import type { Language } from "@/utils/languageUtils";
import { carDetailsLabels } from "@/i18n";

export interface AuctionCalendarEvent {
  title: string;
  make: string;
  model: string;
  year?: number | string;
  regNumber?: string;
  endTime: string;
  /** Listing cuid (always). */
  listingId?: string;
  /** Public slug when available — preferred in calendar links. */
  listingSlug?: string | null;
  description?: string;
  language?: Language;
}

function formatIcsUtc(date: Date): string {
  return date.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}Z$/, "Z");
}

function escapeIcsText(value: string): string {
  return value
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\n/g, "\\n");
}

export function formatCalendarEventTitle(title: string): string {
  const trimmed = title.trim();
  if (!trimmed) return "BILENIA - Auktion";
  if (/^bilenia\s-/i.test(trimmed)) return trimmed;
  return `BILENIA - ${trimmed}`;
}

export function buildAuctionCalendarDescription(
  event: AuctionCalendarEvent,
  listingUrl: string,
  language: Language = "sv",
): string {
  if (event.description) return event.description;

  const bi = carDetailsLabels(language).biddingInfo as Record<string, string>;
  const end = new Date(event.endTime);
  const endFormatted = Number.isNaN(end.getTime())
    ? "—"
    : end.toLocaleString(language === "en" ? "en-SE" : "sv-SE", {
        dateStyle: "medium",
        timeStyle: "short",
      });
  const createdFormatted = new Date().toLocaleString(
    language === "en" ? "en-SE" : "sv-SE",
    { dateStyle: "medium", timeStyle: "short" },
  );

  const vehicleLine = [event.make, event.model, event.year]
    .filter(Boolean)
    .join(" ");

  return [
    bi.addToCalendarDescriptionHeading ?? "Auktion på BILENIA",
    "",
    `${bi.addToCalendarVehicleLabel ?? "Fordon"}: ${vehicleLine}`,
    event.regNumber
      ? `${bi.addToCalendarRegLabel ?? "Reg.nr"}: ${event.regNumber}`
      : "",
    `${bi.addToCalendarEndLabel ?? "Planerat slutdatum"}: ${endFormatted}`,
    `${bi.addToCalendarCreatedLabel ?? "Händelse skapad"}: ${createdFormatted}`,
    "",
    `${bi.addToCalendarLinkLabel ?? "Länk till annons"}:`,
    listingUrl,
    "",
    "—",
    bi.addToCalendarDisclaimer ??
      "Slutdatumet baseras på information vid tillfället då kalenderhändelsen skapades. Slutdatum kan komma att ändras av administratör, eller förlängas automatiskt vid budgivning nära sluttid enligt auktionens regler.",
  ]
    .filter(Boolean)
    .join("\n");
}

export function buildAuctionEndIcs(
  event: AuctionCalendarEvent,
  listingUrl?: string,
): string {
  const end = new Date(event.endTime);
  if (Number.isNaN(end.getTime())) {
    throw new Error("Invalid auction end time");
  }

  const language = event.language ?? "sv";
  const start = new Date(end.getTime() - 30 * 60 * 1000);
  const now = new Date();
  const uid = `bilenia-auction-${event.listingId ?? "listing"}-${end.getTime()}@bilenia.se`;
  const summary = formatCalendarEventTitle(event.title);
  const description = buildAuctionCalendarDescription(
    event,
    listingUrl ?? "https://app.bilenia.se",
    language,
  );
  const bi = carDetailsLabels(language).biddingInfo as Record<string, string>;

  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//BILENIA//Auction Reminder//SV",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${formatIcsUtc(now)}`,
    `DTSTART:${formatIcsUtc(start)}`,
    `DTEND:${formatIcsUtc(end)}`,
    `SUMMARY:${escapeIcsText(summary)}`,
    `DESCRIPTION:${escapeIcsText(description)}`,
    listingUrl ? `URL:${listingUrl}` : "",
    "BEGIN:VALARM",
    "TRIGGER:-PT1H",
    "ACTION:DISPLAY",
    `DESCRIPTION:${escapeIcsText(bi.addToCalendarReminder ?? "Påminnelse: auktion avslutas snart")}`,
    "END:VALARM",
    "END:VEVENT",
    "END:VCALENDAR",
  ].filter(Boolean);

  return `${lines.join("\r\n")}\r\n`;
}

export function buildAuctionCalendarFileName(event: AuctionCalendarEvent): string {
  const reg = event.regNumber?.replace(/[^a-zA-Z0-9-]/g, "") || "auktion";
  return `bilenia-auktion-${reg}.ics`;
}
